import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { PATHS } from "@constants/Paths";
/**
 * Marketing retargeting / conversion pixels (Meta Pixel + LinkedIn Insight Tag) for PROSPECTS.
 *
 * These are ad-platform tags, not product analytics, so they are deliberately kept off every page a
 * logged-in customer uses. They load only on the public prospect routes listed in
 * `MARKETING_PIXEL_ROUTES`, only for a visitor who is not signed in, and only where the pixel ids
 * are configured, which is production alone.
 *
 * `/embed/signup` is intentionally absent: it is iframed inside www.peek.us, which already carries
 * both pixels on the host page.
 */
/**
 * The pixel ids (Meta Pixel for the Peek Technologies business ad account, LinkedIn Insight Tag
 * partner id for the Peek LinkedIn ad account) live in Parameter Store per environment, like
 * `PEEK_APP_GA_MEASUREMENT_ID`: `/<env>/apps/web/ai-discoverability/META_PIXEL_ID` and
 * `.../LINKEDIN_PARTNER_ID`. Only production defines them, so no other environment ever loads a
 * pixel. Read at call time rather than module load so tests can set them per case.
 */
const metaPixelId = () => { var _a; return (_a = process.env.PEEK_APP_META_PIXEL_ID) !== null && _a !== void 0 ? _a : ""; };
const linkedInPartnerId = () => { var _a; return (_a = process.env.PEEK_APP_LINKEDIN_PARTNER_ID) !== null && _a !== void 0 ? _a : ""; };
const META_PIXEL_SRC = "https://connect.facebook.net/en_US/fbevents.js";
const LINKEDIN_INSIGHT_SRC = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
/**
 * Public prospect routes that load the pixels. Exact matches only: nothing under `/o/:orgId` (the
 * authenticated member tree, which is also where checkout lives) may ever be added here.
 */
export const MARKETING_PIXEL_ROUTES = [PATHS.LANDING, PATHS.DEMO];
/** True when `pathname` is one of the public prospect routes that carry the pixels. */
export const isMarketingPixelRoute = (pathname) => MARKETING_PIXEL_ROUTES.some((route) => matchPath({ path: route, end: true }, pathname) !== null);
/**
 * Enabled where at least one pixel id is configured, which is production alone. Localhost stays out
 * regardless (the same guard Sentry uses), so a developer's `.env` can never feed the ad accounts'
 * audiences or conversion counts.
 */
export const marketingPixelsEnabled = () => typeof window !== "undefined" &&
    Boolean(metaPixelId() || linkedInPartnerId()) &&
    !window.location.hostname.includes("localhost");
/** Marks the script tags this module appends, so a later call can see the pixels are already on the page. */
const PIXEL_SCRIPT_ATTRIBUTE = "data-peek-marketing-pixel";
const appendScript = (src) => {
    if (document.querySelector(`script[src="${src}"]`))
        return;
    const script = document.createElement("script");
    script.async = true;
    script.src = src;
    script.setAttribute(PIXEL_SCRIPT_ATTRIBUTE, "");
    document.head.appendChild(script);
};
/** True once this page load has injected the pixels. Read from the DOM, so there is no module state to reset. */
const marketingPixelsLoaded = () => document.querySelector(`script[${PIXEL_SCRIPT_ATTRIBUTE}]`) !== null;
/** The standard fbevents.js bootstrap, typed. Queues calls until the real library arrives. */
const injectMetaPixel = (pixelId) => {
    if (!window.fbq) {
        const fbq = function (...args) {
            if (fbq.callMethod)
                fbq.callMethod(...args);
            else
                fbq.queue.push(args);
        };
        fbq.push = fbq;
        fbq.loaded = true;
        fbq.version = "2.0";
        fbq.queue = [];
        // fbevents.js fires its own PageView on every history.pushState in an SPA. A prospect who
        // submits /demo is logged in and routed into the app, so that auto-tracking would follow them
        // onto authenticated pages. Page views are sent explicitly instead: one below, at injection,
        // and one from `useMarketingPixels` for every pixel route visited after that.
        fbq.disablePushState = true;
        window.fbq = fbq;
        if (!window._fbq)
            window._fbq = fbq;
        appendScript(META_PIXEL_SRC);
    }
    window.fbq("init", pixelId);
    window.fbq("track", "PageView");
};
/** The standard LinkedIn Insight Tag bootstrap, typed. */
const injectLinkedInInsightTag = (partnerId) => {
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    if (!window._linkedin_data_partner_ids.includes(partnerId)) {
        window._linkedin_data_partner_ids.push(partnerId);
    }
    if (!window.lintrk) {
        const lintrk = function (a, b) {
            var _a;
            (_a = lintrk.q) === null || _a === void 0 ? void 0 : _a.push([a, b]);
        };
        lintrk.q = [];
        window.lintrk = lintrk;
    }
    appendScript(LINKEDIN_INSIGHT_SRC);
};
/**
 * Injects every pixel that has an id configured, once per page load. Safe to call repeatedly and a
 * no-op outside a browser or where no id is set. Returns whether this call did the injection.
 */
export const loadMarketingPixels = () => {
    if (typeof window === "undefined" || typeof document === "undefined")
        return false;
    if (marketingPixelsLoaded())
        return false;
    const pixelId = metaPixelId();
    const partnerId = linkedInPartnerId();
    if (!pixelId && !partnerId)
        return false;
    if (pixelId)
        injectMetaPixel(pixelId);
    if (partnerId)
        injectLinkedInInsightTag(partnerId);
    return true;
};
/**
 * Sends a Meta standard event (e.g. `Lead`) if the pixel is on the page. Never loads the pixel
 * itself: a conversion on a route that did not load it is simply not sent to Meta.
 */
export const trackMetaPixelEvent = (event, params) => {
    if (typeof window === "undefined" || typeof window.fbq !== "function")
        return;
    if (params)
        window.fbq("track", event, params);
    else
        window.fbq("track", event);
};
/**
 * Loads the pixels when the current route is a public prospect route and nobody is signed in. The
 * injection sends Meta its first PageView; every later pixel route gets one from here, because
 * Meta's own SPA page views are switched off (see `injectMetaPixel`). Mounted once, next to
 * `usePageTracking`, in `AppRoutes`.
 */
export const useMarketingPixels = (isAuthenticated) => {
    const { pathname } = useLocation();
    useEffect(() => {
        if (isAuthenticated)
            return;
        if (!marketingPixelsEnabled())
            return;
        if (!isMarketingPixelRoute(pathname))
            return;
        if (!loadMarketingPixels())
            trackMetaPixelEvent("PageView");
    }, [pathname, isAuthenticated]);
};
